var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { padLeftZeros, insertAt } from './strings';
var MaskFormat = /** @class */ (function () {
    function MaskFormat(_a) {
        var separator = _a.separator, _b = _a.inputSeparators, inputSeparators = _b === void 0 ? [] : _b, segments = _a.segments;
        this.separator = separator;
        this.inputSeparators = __spreadArrays(inputSeparators, [separator]);
        this.enrichSegmentDefinitions(segments);
    }
    MaskFormat.prototype.tryAppendSeparator = function (value) {
        var withSeparator = "" + value + this.separator;
        return this.isValid(withSeparator) ? withSeparator : value;
    };
    MaskFormat.prototype.isSeparator = function (key) {
        return this.inputSeparators.indexOf(key) !== -1;
    };
    MaskFormat.prototype.isValid = function (value) {
        var _this = this;
        var inputSegments = value.split(this.separator);
        if (inputSegments.length > this.segments.length) {
            return false;
        }
        return inputSegments.every(function (segmentValue, i) {
            var segment = _this.segments[i];
            // disallow empty segments
            if (segmentValue === '') {
                // except empty last segment (e.g. trailing separator "12:")
                if (i === inputSegments.length - 1) {
                    return true;
                }
                else {
                    return false;
                }
            }
            // only allow numerals
            if (!segmentValue.match(/^\d+$/)) {
                return false;
            }
            // disallow incomplete segments, except at end
            if (segmentValue.length < segment.length && i !== inputSegments.length - 1) {
                return false;
            }
            // limit numerical value
            var intValue = parseInt(segmentValue, 10);
            if (intValue < segment.min || intValue > segment.max(value)) {
                // allow incomplete segments in final position
                if (i === inputSegments.length - 1 && segmentValue.length < segment.length) {
                    return true;
                }
                return false;
            }
            return true;
        });
    };
    MaskFormat.prototype.autoComplete = function (value) {
        // aka [...completeSegments, lastSegment] = value.split(':')
        // but that's not valid :/
        var _a = value.split(this.separator).reverse(), lastSegmentValue = _a[0], completeSegmentValues = _a.slice(1);
        var lastSegment = this.segments[completeSegmentValues.length];
        // if the last segment isn't complete, pad it with a preceding 0
        // e.g. 10:1 -> 10:01
        lastSegmentValue = this.padWithDefaultValue(lastSegmentValue, lastSegment);
        // recombine, and pad with extra segments for the full format
        var partial = __spreadArrays(completeSegmentValues.reverse(), [lastSegmentValue]);
        while (partial.length < this.segments.length) {
            var nextSegment = this.segments[partial.length];
            var segmentValue = this.padWithDefaultValue('', nextSegment);
            partial.push(segmentValue);
        }
        value = partial.join(this.separator);
        value = this.correctMinMaxValues(value);
        return value;
    };
    MaskFormat.prototype.getSegmentValueWithAddition = function (position, value, enteredDigit) {
        var segment = this.positionFormats[position];
        var segmentValue = value.substr(segment.start, segment.length);
        var segmentPosition = position - segment.start;
        var newValue = insertAt(segmentValue, enteredDigit, segmentPosition, segmentPosition + 1);
        return parseInt(newValue, 10);
    };
    MaskFormat.prototype.replaceDigitsWithZeroes = function (value, cursorStart, cursorEnd) {
        var position = this.isCursorAtSeparator(cursorStart) ? cursorStart + 1 : cursorStart;
        // move selection forwards if it starts with a separator
        if (this.isCursorAtSeparator(cursorStart)) {
            cursorStart++;
        }
        // first, insert zeros in a partial segment at beginning of selection
        if (!this.isSegmentStart(cursorStart)) {
            var segment = this.positionFormats[cursorStart];
            value = insertAt(value, padLeftZeros('', segment.end - cursorStart), cursorStart, segment.end);
            cursorStart = segment.end + 1;
        }
        // then loop through remaining segments, filling with zeros
        var currentSegment;
        while (cursorStart < cursorEnd && (currentSegment = this.positionFormats[cursorStart + 1])) {
            var insertionEnd = Math.min(cursorEnd, currentSegment.end);
            value = insertAt(value, padLeftZeros('', insertionEnd - currentSegment.start), currentSegment.start, insertionEnd);
            cursorStart = insertionEnd + 1;
        }
        value = this.correctMinMaxValues(value);
        return {
            value: value,
            position: position
        };
    };
    MaskFormat.prototype.handleSeparatorInput = function (value, position) {
        if (position === value.length && !this.isSegmentStart(position)) {
            var segment = this.positionFormats[position];
            var segmentValue = value.substr(segment.start, segment.length);
            segmentValue = this.padWithDefaultValue(segmentValue, segment);
            value = insertAt(value, segmentValue, segment.start, segment.end);
            value = this.correctMinMaxValues(value);
            return {
                value: value,
                position: value.length
            };
        }
    };
    MaskFormat.prototype.isCursorAtSeparator = function (position) {
        return 0 < position && position < this.getMaxLength() && this.positionFormats[position] === undefined;
    };
    MaskFormat.prototype.isSegmentStart = function (position) {
        return position === 0 || this.isCursorAtSeparator(position - 1);
    };
    MaskFormat.prototype.getSegmentMaxValue = function (value, position) {
        return this.positionFormats[position].max(value);
    };
    MaskFormat.prototype.getSegmentMinValue = function (value, position) {
        return this.positionFormats[position].min;
    };
    MaskFormat.prototype.getMaxLength = function () {
        var last = this.segments[this.segments.length - 1];
        return last.start + last.length;
    };
    MaskFormat.prototype.deleteSeparator = function (value, position) {
        value = insertAt(value, '0', position - 2, position - 1);
        return {
            value: this.correctMinMaxValues(value),
            position: position - 2
        };
    };
    MaskFormat.prototype.deleteDigit = function (value, position) {
        value = insertAt(value, '0', position - 1, position);
        // 23:59|: => backspace => 23:5|
        var length = value.length;
        if (value.slice(length - 2) === '0:') {
            value = value.slice(0, length - 2);
        }
        return {
            value: this.correctMinMaxValues(value),
            position: position - 1
        };
    };
    MaskFormat.prototype.correctMinMaxValues = function (value) {
        var segment = this.positionFormats[0];
        while (segment && value.length >= segment.end) {
            var segmentValue = parseInt(value.substr(segment.start, segment.length), 10);
            var segmentMax = segment.max(value);
            if (segmentValue < segment.min) {
                var toInsert = segment.min.toFixed();
                toInsert = padLeftZeros(toInsert, segment.length);
                value = insertAt(value, toInsert, segment.start, segment.end);
            }
            if (segmentValue > segmentMax) {
                value = insertAt(value, segmentMax.toFixed(), segment.start, segment.end);
            }
            segment = this.positionFormats[segment.end + 1];
        }
        return value.substr(0, this.segments[this.segments.length - 1].end);
    };
    MaskFormat.prototype.padWithDefaultValue = function (segmentValue, segment) {
        var defaultValue = (segment.default || segment.min).toFixed();
        defaultValue = padLeftZeros(defaultValue, segment.length);
        return insertAt(defaultValue, segmentValue, segment.length - segmentValue.length, segment.length);
    };
    MaskFormat.prototype.enrichSegmentDefinitions = function (segments) {
        this.positionFormats = {};
        this.segments = [];
        var position = 0;
        var _loop_1 = function (segment) {
            var max = segment.max;
            var fullSegment = __assign(__assign({}, segment), { max: typeof max === 'number' ? function () { return max; } : max, start: position, end: position + segment.length });
            this_1.segments.push(fullSegment);
            // insert this format segment for every char in the max value
            for (var j = 0; j < fullSegment.length; j++) {
                this_1.positionFormats[position++] = fullSegment;
            }
            // skip a position for separator
            position++;
        };
        var this_1 = this;
        for (var _i = 0, segments_1 = segments; _i < segments_1.length; _i++) {
            var segment = segments_1[_i];
            _loop_1(segment);
        }
    };
    return MaskFormat;
}());
export default MaskFormat;
//# sourceMappingURL=MaskFormat.js.map